import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { IMapEnquiryBadge } from '@lassi/webapp-shared';

@Component({
  selector: 'lib-option-card',
  imports: [MatCardModule, MatButtonModule, MatIconModule, RouterLink],
  template: `
    <a
      [routerLink]="cardRouterLink()"
      [queryParams]="linkQueryParams()"
      [attr.aria-label]="title()"
    >
      <mat-card class="option-card">
        <mat-card-content class="grid">
          <div
            class="icon"
            [style.background-color]="iconColor()"
            aria-hidden="true"
          ></div>
          <div class="text">
            <div class="title-row">
              <h3 class="title">
                {{ title() }}
                @if (buttonText()) {
                  <span class="button-text">{{ buttonText() }}</span>
                }
              </h3>
              <div class="badge-container">
                @for (badge of badges(); track badge.text) {
                  <span class="badge" [style.background-color]="badge.color">{{
                    badge.text
                  }}</span>
                }
              </div>
            </div>
            <p class="description">{{ description() }}</p>
          </div>
          <mat-icon class="arrow" aria-hidden="true"> chevron_right </mat-icon>
        </mat-card-content>
      </mat-card>
    </a>
  `,
  styles: [
    `
      @use 'breakpoints' as *;

      a {
        text-decoration: none;
        color: inherit;
        display: block;
        border-radius: 12px;
        outline: none;
      }

      .option-card {
        box-shadow: none;
        transition: background-color 0.3s ease;
        padding: 0;
        cursor: pointer;

        &:hover {
          background-color: #e0f0ff;
        }

        .grid {
          display: grid;
          grid-template-columns: auto 1fr auto;
          grid-template-areas: 'icon text arrow';
          align-items: center;
          gap: 12px;
          padding: 16px;
        }

        .icon {
          grid-area: icon;
          font-size: 24px;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
        }

        .text {
          grid-area: text;
          align-self: start;
          display: flex;
          flex-direction: column;
          gap: 4px;
          justify-content: flex-start;
        }

        .title {
          font-size: 15px;
          font-weight: 600;
          display: inline-block;
          margin: 0;

          @include tablet {
            font-size: 16px;
          }
        }

        .description {
          font-size: 12px;
          font-weight: 450;
          display: inline-block;
          margin: inherit;
          line-height: inherit;

          @include tablet {
            font-size: 16px;
          }
        }

        .arrow {
          grid-area: arrow;
          width: 24px;
          color: #46464f;

          @include tablet {
            display: none;
          }
        }

        .title-row {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          align-items: center;

          .badge-container {
            display: flex;
            gap: 0.5rem;
          }
        }

        .badge {
          display: none;
          font-size: 12px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 8px;
          color: #003733;
          height: 15px;

          @include tablet {
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        }

        .button-text {
          color: var(--link-text-color);
          font-weight: 700;

          @include tablet {
            margin-left: 8px;
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionCardComponent {
  title = input.required<string>();
  description = input.required<string>();
  iconColor = input<string>('white');
  badges = input.required<IMapEnquiryBadge[]>();
  buttonText = input<string | undefined>(undefined);
  cardRouterLink = input.required<string | string[]>();
  linkQueryParams = input<{ [k: string]: string } | undefined>(undefined);
}
